import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  CustomCollapseCard,
  CustomDatePicker,
  CustomForm,
  CustomFormItem, CustomInput,
  CustomSelect,
  errorDialog,
  Option,
  Text,
  useText,
} from '@src/components';
import { monthsList } from '@src/store/enums';
import dayjs from '@src/utils/day';
import '@src/styles/balanceUpload/addInformation.scss';
import { useDispatch, useSelector } from 'react-redux';
import { loadBalancesUpdate, setIsMaxValuePopupOpen } from '@src/store/slice/balanceUploadSlice';
import PropTypes from 'prop-types';

const AdditionalInformation = ({ form }) => {
  const { currentLoadBalance, runTimeTotalPrice } = useSelector((state) => state?.balanceUpload);
  const [isDateControl, setIsDateControl] = useState(false);
  const dispatch = useDispatch();
  const currentDate = dayjs();
  const selectChoose = useText('choose');
  const eBillInformation = useText('pleaseEnter');
  const orderContinueInstruction = useText('order.continueInstruction');
  const balanceActivationDateRequired = useText('balanceActivationDateRequired');
  const youCannotEnterAPastDate = useText('youCannotEnterAPastDate');
  const changedDateRef = useRef(false);

  useEffect(() => {
    const currentDay = currentDate?.date();
    const currentMonth = currentDate?.month() + 1;
    const controlDate = currentDay >= 15;
    setIsDateControl(controlDate);

    const responseInvoiceMonth =
      currentLoadBalance?.invoiceMonth !== 0 ? currentLoadBalance?.invoiceMonth : undefined;
    const invoiceMonth = !controlDate ? currentMonth : responseInvoiceMonth || currentMonth;

    let nextM = currentMonth === 12 ? 1 : currentMonth + 1;
    let filterData = monthsList.filter((item) => [currentMonth, nextM].includes(item.value));
    if (filterData.length === 1) {
      setIsDateControl(false);
    }
    const activationDate = dayjs(currentLoadBalance?.activationDate);
    let isBefore = activationDate.isBefore(currentDate, 'day') || (!changedDateRef.current && activationDate.isSame(currentDate, 'day'));
    if (currentLoadBalance?.pendingApproval) {
      isBefore = activationDate.isBefore(currentDate, 'day');
    }

    form.setFieldsValue({
      invoiceMonth: invoiceMonth,
      eInvoiceInformation: currentLoadBalance?.eInvoiceInformation,
      orderType: currentLoadBalance?.orderType,
      activationDate: isBefore ? null : activationDate,
    });
  }, [currentLoadBalance, form, isDateControl]);


  const disabledDate = useCallback((current) => {
    return current && current < dayjs().startOf('day');
  }, []);

  const MountSelectOptions = () => {
    const currentMonth = currentDate?.month() + 1;
    let nextMonth = currentMonth === 12 ? 1 : currentMonth + 1;

    let filterData = monthsList.filter((item) =>
      [...new Set([currentLoadBalance?.invoiceMonth, currentMonth, nextMonth])].includes(
        item.value,
      ),
    );

    if (!isDateControl) {
      filterData = monthsList.filter((item) => [currentMonth].includes(item.value));
    }
    if (currentMonth === 12) {
      filterData = filterData.reverse();
    }

    return filterData.map((month) => {
      return (
        <Option
          id={`ticketRestaurant-balanceUpload-balanceInformation-additionalInformation-invoiceMonth-${month.text}-option`}
          key={month.value}
          value={month.value}
        >
          {month.text}
        </Option>
      );
    });
  };

  const pastDateValidator = async (_field, value) => {
    if (value === null && runTimeTotalPrice === 0) {
      errorDialog({
        title: <Text t="balanceApproval" />,
        message: orderContinueInstruction,
      });
      return Promise.resolve();
    }
    if (value !== null) {
      try {
        const now = new Date();
        const today = dayjs(now.toDateString());
        const selectedDate = dayjs(value);
        const isBefore = selectedDate.isBefore(today, 'day');
        if (isBefore) {
          errorDialog({
            title: <Text t="error" />,
            message: youCannotEnterAPastDate,
          });
          return Promise.reject(new Error('error'));
        }
        return Promise.resolve();
      } catch (e) {
        return Promise.reject(new Error('error'));
      }
    } else {
      errorDialog({
        title: <Text t="error" />,
        message: balanceActivationDateRequired,
      });
      return Promise.resolve();
    }
  };

  const activationDateValidator = async (_field, value) => {
    if (value !== null) {
      try {
        const values = await form.getFieldsValue();
        let sysDateYear = new Date().getFullYear();
        let sysDateMonth = new Date().getMonth() + 1;

        if (value.month() + 1 === values.invoiceMonth && value.year() === sysDateYear) {
          return Promise.resolve();
        }

        if (
          sysDateMonth === 12 &&
          values.invoiceMonth === 1 &&
          value.month() + 1 === values.invoiceMonth &&
          value.year() === sysDateYear + 1
        ) {
          return Promise.resolve();
        }

        return Promise.reject(new Error('error'));
      } catch (e) {
        return Promise.reject(new Error('error'));
      }
    } else {
      return Promise.resolve();
    }
  };

  const activationDateChange = async (value) => {
    if (!value) return;
    changedDateRef.current = true;
    const activationDateIso = dayjs(value).utcOffset(0).set('hour', 9).set('minute', 0).set('second', 0).toISOString();
    await dispatch(loadBalancesUpdate({ ...currentLoadBalance, activationDate: activationDateIso, orderStatus: 0 }));
    await dispatch(setIsMaxValuePopupOpen(false));
    if (currentLoadBalance?.invoiceMonth !== dayjs(value).month() + 1) {
      await form.validateFields(['activationDate', 'invoiceMonth']);
    }
  }

  const activeMountChange = async (value) => {
    await dispatch(loadBalancesUpdate({ ...currentLoadBalance, invoiceMonth: value, orderStatus: 0 }));
    if (value !== currentLoadBalance?.invoiceMonth) {
      await form.validateFields(['activationDate', 'invoiceMonth']);
    }
  }

  return (
    <CustomCollapseCard cardTitle={useText('carOrderInformation')} className="add-information-balance-upload">
      <div className="add-information-container">
        <CustomForm
          name="additionalInformation"
          className="addInformationForm"
          initialValues={{
            orderType: 0,
          }}
          autoComplete="off"
          layout="horizontal"
          form={form}
        >
          <div className="form-item">
            <CustomFormItem
              label={useText('invoiceMonth')}
              name="invoiceMonth"
              className={'bill-month-form-item'}
              rules={[{ required: true, message: <Text t="requiredField2" /> }]}
            >
              <CustomSelect
                id="ticketRestaurant-balanceUpload-balanceInformation-additionalInformation-invoiceMonth-select"
                placeholder={selectChoose}
                disabled={!isDateControl}
                onChange={activeMountChange}
                style={{ width: 89, marginLeft: '8px' }}
              >
                {MountSelectOptions()}
              </CustomSelect>
            </CustomFormItem>
            <CustomFormItem
              label={<Text t="balanceActivationDate2" />}
              name="activationDate"
              className="active-date-form-item"
              rules={[
                { required: true, message: <Text t="requiredField2" /> },
                {
                  validator: pastDateValidator,
                  message: <Text t="youCannotEnterAPastDate" />,
                },
                {
                  validator: activationDateValidator,
                  message: <Text t="balanceDateAndInvoiceMonthMustBeCompatible" />,
                },
              ]}
            >
              <CustomDatePicker
                id="ticketRestaurant-balanceUpload-balanceInformation-additionalInformation-activationDate-datePicker"
                placeholder={useText('pickDate')}
                onChange={activationDateChange}
                disabledDate={disabledDate}
              />
            </CustomFormItem>
            <CustomFormItem
              label={useText('eInvoiceAdditionalInfo')}
              name="eInvoiceInformation"
              className={'delivery-address-add-form-item'}
            >
              <CustomInput
                id="ticketRestaurant-balanceUpload-balanceInformation-additionalInformation-eInvoiceInformation-textInput"
                placeholder={eBillInformation}
                maxLength={150}
                hasNumberChar={true}
              />
            </CustomFormItem>

            <CustomFormItem label={<Text t="invoiceAddress2" />} className={'delivery-address-form-item'}>
              <span className="address-info-box">
                {currentLoadBalance?.invoiceAddress?.length > 14
                  ? `${currentLoadBalance.invoiceAddress.substring(0, 14)}...`
                  : currentLoadBalance?.invoiceAddress}
              </span>
            </CustomFormItem>
          </div>
        </CustomForm>
      </div>
    </CustomCollapseCard>
  );
};
AdditionalInformation.propTypes = {
  form: PropTypes.any
};


export default AdditionalInformation;
